import { Button, ButtonGroup, Modal, LegacyStack, Text, TextField } from '@shopify/polaris';
import { dateToFileFormat, dateToHumanReadableFormat } from '../../../helpers/dateConverter';
import { useContext, useEffect, useState } from 'react';
import ToastContext from '../../../context/ToastContext';
import { SET_ERROR, SET_OK } from '../../../context/ToastReducer';
import { postPDF } from '../../../services/pdfService';
import UserContext from '../../../context/UserContext';
import { NEW_FILE_FAILED, NEW_FILE_LOADED, NEW_FILE_LOADING } from '../../../context/UserReducer';

export const PDFModal = ({
  title,
  subtitle,
  active,
  onClose,
  domain,
  model,
  query,
  AttributesWrapper,
  attributesDefault = {},
  availableTemplates = ['list', 'book'],
}) => {
  const [template, setTemplate] = useState(availableTemplates[0] ?? 'list');
  const [attributes, setAttributes] = useState(attributesDefault);
  const [, dispatchToast] = useContext(ToastContext);
  const [, dispatchUser] = useContext(UserContext);

  const dispatchNewFile = (file) => {
    dispatchUser({ type: NEW_FILE_LOADING, payload: file });
  };

  const dispatchNewFileLoaded = (payload) => {
    dispatchUser({ type: NEW_FILE_LOADED, payload });
  };

  const dispatchNewFileFailed = (id) => {
    dispatchUser({ type: NEW_FILE_FAILED, payload: { id } });
  };

  const generateParams = () => ({
    title: title ?? `INFORME DE ${model.toUpperCase()}`,
    subtitle: subtitle ?? `${dateToHumanReadableFormat(new Date())}`,
    filename: `${domain}_${model}_${dateToFileFormat(new Date())}.pdf`,
    attributes: {
      ...{
        cover: true,
        showNumbers: true,
        showPrices: false,
        showComposition: false,
      },
      ...attributes,
    },
    query,
  });

  const [params, setParams] = useState(generateParams());

  useEffect(() => {
    setParams({ ...params, query });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    setParams({ ...params, attributes });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes]);

  useEffect(() => {
    setParams(generateParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const handleExport = () => {
    dispatchToast({ type: SET_OK, payload: { message: 'Generando PDF, puede tardar' } });
    dispatchNewFile({
      id: params.filename,
      type: `${model.toUpperCase()}`,
      name: params.title,
      date: new Date(),
    });
    onClose();
    postPDF(domain, model, template, params)
      .then((r) => {
        dispatchToast({ type: SET_OK, payload: { message: 'PDF Generado' } });
        dispatchNewFileLoaded({
          id: params.filename,
          url: r.data.path,
          validUntil: r.data.validUntil,
        });
      })
      .catch((e) => {
        dispatchToast({ type: SET_ERROR, payload: e.response.data });
        dispatchNewFileFailed(params.filename);
      });
  };

  return (
    <Modal
      open={active}
      onClose={onClose}
      title="Exportar en PDF"
      primaryAction={{
        content: 'Exportar',
        onAction: handleExport,
      }}
      secondaryActions={[
        {
          content: 'Cancelar',
          onAction: onClose,
        },
      ]}
    >
      <Modal.Section>
        <LegacyStack horizontal distribution="fill">
          <LegacyStack.Item>
            <Text variant="bodyMd">Formato</Text>
            <ButtonGroup segmented>
              {availableTemplates.includes('list') && (
                <Button primary={template === 'list'} onClick={() => setTemplate('list')}>
                  Lista
                </Button>
              )}
              {availableTemplates.includes('book') && (
                <Button primary={template === 'book'} onClick={() => setTemplate('book')}>
                  Libro
                </Button>
              )}
            </ButtonGroup>
          </LegacyStack.Item>
          <TextField
            label="Nombre del fichero"
            value={params.filename}
            onChange={(value) => setParams({ ...params, filename: value })}
            autoComplete="off"
          />
        </LegacyStack>
        <br />
        <LegacyStack vertical></LegacyStack>
      </Modal.Section>
      <Modal.Section>
        <LegacyStack vertical>
          <LegacyStack.Item>
            <TextField
              label="Título"
              value={params.title}
              onChange={(value) => setParams({ ...params, title: value })}
              autoComplete="off"
            />
            <TextField
              label="Subtítulo"
              value={params.subtitle}
              onChange={(value) => setParams({ ...params, subtitle: value })}
              autoComplete="off"
            />
          </LegacyStack.Item>
          <LegacyStack.Item>
            {!AttributesWrapper && (
              <>
                <Text variant="bodyMd">Propiedades</Text>
                <ButtonGroup spacing={'tight'}>
                  <Button
                    primary={params.attributes.cover}
                    onClick={() =>
                      setParams({
                        ...params,
                        attributes: {
                          ...params.attributes,
                          cover: !params.attributes.cover,
                        },
                      })
                    }
                  >
                    Incluir portada
                  </Button>
                  <Button
                    primary={params.attributes.showNumbers}
                    onClick={() =>
                      setParams({
                        ...params,
                        attributes: {
                          ...params.attributes,
                          showNumbers: !params.attributes.showNumbers,
                        },
                      })
                    }
                  >
                    Incluir números
                  </Button>
                  <Button
                    primary={params.attributes.showPrices}
                    onClick={() =>
                      setParams({
                        ...params,
                        attributes: {
                          ...params.attributes,
                          showPrices: !params.attributes.showPrices,
                        },
                      })
                    }
                  >
                    Mostrar PVP
                  </Button>
                  <Button
                    primary={params.attributes.showComposition}
                    onClick={() =>
                      setParams({
                        ...params,
                        attributes: {
                          ...params.attributes,
                          showComposition: !params.attributes.showComposition,
                        },
                      })
                    }
                  >
                    Incluir Composición
                  </Button>
                </ButtonGroup>
              </>
            )}

            {AttributesWrapper && (
              <AttributesWrapper
                attr={attributes}
                onAttrChange={(e) => {
                  if (typeof e === 'object' && e !== null && !Array.isArray(e))
                    setAttributes({
                      ...attributes,
                      ...e,
                    });
                }}
              />
            )}
          </LegacyStack.Item>
        </LegacyStack>
      </Modal.Section>
    </Modal>
  );
};
